import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/rbac";
import { localName, localFullName } from "../lib/localName";

export default function GradesEntryPage() {
  const {  t , lang } = useI18n();
  const { user } = useAuth();
  const canEnter = can.enterGrades(user);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [school, setSchool] = useState("");
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [term, setTerm] = useState("T1");
  const [assessment, setAssessment] = useState("exam");
  const [maxScore, setMaxScore] = useState(100);
  const [weight, setWeight] = useState(1);
  const [scores, setScores] = useState({});

  useEffect(() => { api.get("/schools").then(r=>setSchools(r.data||[])); }, []);
  useEffect(() => { if (school) {
    api.get(`/classes?school_id=${school}`).then(r=>setClasses(r.data||[]));
  } }, [school]);
  // Iter45: reload subjects when class changes so we only show subjects taught
  // in that class (subjects with empty class_ids are treated as "all classes").
  useEffect(() => {
    if (!school) { setSubjects([]); return; }
    const url = classId
      ? `/subjects?school_id=${school}&class_id=${classId}`
      : `/subjects?school_id=${school}`;
    api.get(url).then(r => setSubjects(r.data || []));
    setSubjectId("");
  }, [school, classId]);
  useEffect(() => {
    if (classId) api.get(`/students?class_id=${classId}`).then(r=>setStudents(r.data||[]));
  }, [classId]);
  useEffect(() => {
    if (classId && subjectId) {
      api.get(`/grades-entry?class_id=${classId}&subject_id=${subjectId}&term=${term}`).then(r => {
        const map = {};
        (r.data||[]).filter(e=>e.assessment===assessment).forEach(e => map[e.student_id] = e.score);
        setScores(map);
      });
    }
  }, [classId, subjectId, term, assessment]);

  const submit = async () => {
    const entries = students.map(s => ({ student_id: s.id, score: parseFloat(scores[s.id]||0) }));
    await api.post("/grades-entry", { school_id: school, class_id: classId, subject_id: subjectId, term, assessment, weight: parseFloat(weight), max_score: parseFloat(maxScore), entries });
    toast.success(t("success"));
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-semibold">{t("gradesEntry")}</h1>
      <Card className="p-4 border shadow-none">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          <Select value={school} onValueChange={setSchool}><SelectTrigger><SelectValue placeholder={t("select_school")}/></SelectTrigger><SelectContent>{schools.map(s=><SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent></Select>
          <Select value={classId} onValueChange={setClassId}><SelectTrigger data-testid="ge-class"><SelectValue placeholder={t("select_class")}/></SelectTrigger><SelectContent>{classes.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
          <Select value={subjectId} onValueChange={setSubjectId}><SelectTrigger data-testid="ge-subject"><SelectValue placeholder={t("select_subject")}/></SelectTrigger><SelectContent>{subjects.map(s=><SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent></Select>
          <Select value={term} onValueChange={setTerm}><SelectTrigger data-testid="ge-term"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="T1">{t("term_t1")}</SelectItem><SelectItem value="T2">{t("term_t2")}</SelectItem><SelectItem value="T3">{t("term_t3")}</SelectItem></SelectContent></Select>
          <Select value={assessment} onValueChange={setAssessment}><SelectTrigger data-testid="ge-assessment"><SelectValue/></SelectTrigger><SelectContent>
            <SelectItem value="exam">{t("assessment_exam")}</SelectItem><SelectItem value="quiz">{t("assessment_quiz")}</SelectItem><SelectItem value="hw">{t("assessment_hw")}</SelectItem><SelectItem value="participation">{t("assessment_participation")}</SelectItem>
          </SelectContent></Select>
          <Input type="number" value={maxScore} onChange={e=>setMaxScore(e.target.value)} placeholder={t("max_score_placeholder")} />
          <Input type="number" step="0.1" value={weight} onChange={e=>setWeight(e.target.value)} placeholder={t("weight_placeholder")} />
        </div>
      </Card>

      {students.length > 0 && (
        <Card className="border shadow-none overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr><th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("name")}</th><th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("score")}</th></tr>
            </thead>
            <tbody>
              {students.map((s,i) => (
                <tr key={s.id} className={`border-t border-border ${i%2 ? "bg-muted/20" : ""}`} data-testid={`ge-row-${s.id}`}>
                  <td className="px-4 py-2">{localFullName(s, lang)}</td>
                  <td className="px-4 py-2"><Input type="number" value={scores[s.id]||""} onChange={e=>setScores({...scores, [s.id]: e.target.value})} className="max-w-32" disabled={!canEnter} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {canEnter && <div className="p-4 border-t border-border flex justify-end"><Button onClick={submit} data-testid="ge-save">{t("save_grades")}</Button></div>}
        </Card>
      )}
    </div>
  );
}
